import React from 'react';
import { Link } from 'react-router-dom';
import { CloudRain } from '@phosphor-icons/react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <CloudRain size={32} weight="bold" className="text-blue-600" />
              <span className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold tracking-tighter text-slate-950 mb-8" style={{ fontFamily: 'Chivo' }}>Privacy Policy</h1>
        
        {/* Summary Box */}
        <div className="mb-12 p-6 bg-blue-50 border border-blue-200">
          <h2 className="text-lg font-semibold text-blue-900 mb-4" style={{ fontFamily: 'Chivo' }}>Privacy Policy (Summary)</h2>
          <p className="text-sm text-blue-700 mb-3">WeatherProof collects only the information necessary to provide its services:</p>
          <ul className="space-y-2 text-sm text-blue-700">
            <li>• Email address (for account access)</li>
            <li>• Location and date inputs (for weather reports)</li>
            <li>• Payment information is securely handled by third-party providers (e.g., Stripe)</li>
          </ul>
          <p className="text-sm text-blue-700 mt-3 mb-2">We do not sell or share your personal data.</p>
          <p className="text-sm text-blue-700 mb-2">Data may be used to:</p>
          <ul className="space-y-1 text-sm text-blue-700 ml-4 mb-3">
            <li>• Generate reports</li>
            <li>• Improve service performance</li>
            <li>• Provide customer support</li>
          </ul>
          <p className="text-sm text-blue-700 mb-2">You may request account deletion at any time.</p>
          <p className="text-sm text-blue-700">WeatherProof uses secure connections (HTTPS) to protect your data.</p>
          <p className="text-xs text-blue-600 mt-4 italic">Full privacy policy detailed below.</p>
        </div>
        
        <div className="space-y-8 text-sm text-slate-600 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>1. Information We Collect</h2>
            <p>We collect the following information when you use WeatherProof:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Account Information:</strong> Email address, name, password (encrypted)</li>
              <li><strong>Query Data:</strong> Locations searched, dates, times, and weather data retrieved</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)</li>
              <li><strong>Usage Data:</strong> IP address, browser type, access times</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>2. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide and improve our weather verification services</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send service-related notifications (report generation, account updates)</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>3. Data Storage and Security</h2>
            <p>Your data is stored securely using industry-standard encryption. We implement:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Encrypted password storage using bcrypt</li>
              <li>HTTPS encryption for all data transmission</li>
              <li>Secure database access controls</li>
              <li>Regular security audits and updates</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>4. Data Sharing</h2>
            <p>We do not sell your personal information. We share data only with:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Weather Data Providers:</strong> To retrieve historical weather information</li>
              <li><strong>Payment Processors:</strong> Stripe for payment processing</li>
              <li><strong>Email Services:</strong> SendGrid for transactional emails</li>
              <li><strong>Legal Requirements:</strong> When required by law or legal process</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>5. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Access your personal data</li>
              <li>Request data correction or deletion</li>
              <li>Export your query history (Pro users)</li>
              <li>Opt-out of marketing communications</li>
              <li>Cancel your subscription at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>6. Security</h2>
            <p className="mb-3">We implement industry-standard security measures to protect your data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>HTTPS Encryption:</strong> All data transmission is encrypted using secure HTTPS connections</li>
              <li><strong>Password Security:</strong> Passwords are encrypted using bcrypt hashing (never stored in plain text)</li>
              <li><strong>Payment Security:</strong> Credit card information is handled exclusively by Stripe (PCI-DSS compliant)</li>
              <li><strong>Database Security:</strong> Secure database access controls and authentication</li>
              <li><strong>Regular Updates:</strong> Security patches and updates applied promptly</li>
            </ul>
            <p className="mt-3 text-xs italic">
              Note: While we implement strong security measures, no system is 100% secure. Users are responsible for 
              maintaining the confidentiality of their account credentials.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>7. Data Retention and Deletion</h2>
            <p className="mb-3">
              <strong>Account Data:</strong> We retain your data for as long as your account is active. Query logs and reports 
              are retained for audit purposes and legal compliance.
            </p>
            <p className="mb-3">
              <strong>Request Account Deletion:</strong> You may request account deletion at any time by emailing 
              privacy@weatherproof.com with "Delete My Account" in the subject line. We will:
            </p>
            <ul className="list-disc pl-6 mb-3 space-y-1">
              <li>Delete your account within 30 days</li>
              <li>Remove your personal information (email, name)</li>
              <li>Anonymize query logs (dissociate from your identity)</li>
              <li>Retain purchased reports with anonymized IDs (for legal/audit purposes)</li>
            </ul>
            <p className="mb-3">
              <strong>What We Cannot Delete:</strong> Generated reports with unique IDs must be retained for verification 
              purposes and potential disputes. These will be anonymized (no personal info attached).
            </p>
            <p>
              <strong>Backup Retention:</strong> Data may persist in backups for up to 90 days after deletion request.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>8. Children's Privacy</h2>
            <p>WeatherProof is not intended for users under 18. We do not knowingly collect information from children.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>9. Changes to Privacy Policy</h2>
            <p>We may update this policy from time to time. Continued use of the service constitutes acceptance of updated policies.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>10. Contact</h2>
            <p>For privacy-related questions or data requests, contact us at: privacy@weatherproof.com</p>
          </section>

          <div className="mt-12 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
